import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaBell,
  FaEnvelope,
  FaFacebookF,
  FaInstagram,
  FaMoon,
  FaPhone,
  FaRegEnvelope,
  FaSignInAlt,
  FaSignOutAlt,
  FaSun,
  FaUser,
  FaUserPlus,
  FaUserShield,
  FaTiktok,
} from "react-icons/fa";
import { MdOutlineMessage } from "react-icons/md";

import { toAbsoluteUserPhotoUrl } from "../../../services/userPhotoServices";
import { FALLBACK_AVATAR } from "./navbarData";

const getUserInitials = (user) => {
  const firstName = user?.prenom || user?.first_name || "";
  const lastName = user?.nom || user?.last_name || "";
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.trim();

  if (initials) return initials.toUpperCase();
  if (user?.email) return user.email.charAt(0).toUpperCase();

  return "U";
};

function TopBarBadge({ count }) {
  if (!count || Number(count) <= 0) return null;

  return (
    <span className="absolute -right-1 -top-0 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 my-1 text-[10px] font-bold leading-none text-white shadow-sm ring-2 ring-slate-950">
      {Number(count) > 99 ? "99+" : count}
    </span>
  );
}

function UserAvatar({ user }) {
  const initials = useMemo(() => getUserInitials(user), [user]);

  const rawPhoto =
    user?.photoURL ||
    user?.photo_url ||
    user?.photo ||
    user?.avatar ||
    FALLBACK_AVATAR;

  const photoUrl = toAbsoluteUserPhotoUrl(rawPhoto);

  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt="Utilisateur"
        className="h-8 w-8 rounded-full object-cover ring-1 ring-white/30"
        onError={(event) => {
          event.currentTarget.src = FALLBACK_AVATAR;
        }}
      />
    );
  }

  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-white">
      {initials}
    </div>
  );
}

function TopBarIcon({ children, label, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`relative flex h-9 w-9 items-center justify-center overflow-visible rounded-full text-slate-300 transition hover:bg-white/10 hover:text-white ${className}`}
    >
      {children}
    </button>
  );
}

export default function NavbarTopBar({
  settings,
  user,
  isAuthenticated,
  isAdmin,
  theme,
  unreadCount,
  unreadMessageCount,
  unreadCountContactForm,
  bumpMessages,
  bumpNotifications,
  bumpContactForms,
  onThemeToggle,
  onLogout,
}) {
  const navigate = useNavigate();
  const userMenuRef = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const userFullName =
    `${user?.nom || ""} ${user?.prenom || ""}`.trim() ||
    `${user?.prenom || ""} ${user?.nom || ""}`.trim() ||
    user?.nom_complet ||
    user?.name ||
    user?.email ||
    "Utilisateur";

  const userRole = isAdmin ? "Administrateur" : "Utilisateur";

  useEffect(() => {
    const onClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  return (
    <div className="hidden h-11 overflow-visible bg-slate-950 text-white md:block">
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-4 px-4 lg:px-8">
        <div className="flex min-w-0 items-center gap-4 text-xs font-semibold text-slate-300">
          {settings.mail_club && (
            <button
              type="button"
              onClick={() => {
                window.location.href = `mailto:${settings.mail_club}`;
              }}
              className="inline-flex items-center gap-2 transition hover:text-white"
            >
              <FaEnvelope className="text-[11px]" />
              <span className="hidden lg:inline">{settings.mail_club}</span>
            </button>
          )}

          {settings.tel_club && (
            <button
              type="button"
              onClick={() => {
                window.location.href = `tel:${settings.tel_club}`;
              }}
              className="inline-flex items-center gap-2 transition hover:text-white"
            >
              <FaPhone className="text-[11px]" />
              <span className="hidden lg:inline">{settings.tel_club}</span>
            </button>
          )}

          {settings.facebook_club && (
            <button
              type="button"
              onClick={() =>
                window.open(
                  settings.facebook_club,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="transition hover:text-white"
              aria-label="Facebook"
              title="Facebook"
            >
              <FaFacebookF />
            </button>
          )}

          {settings.instagram_club && (
            <button
              type="button"
              onClick={() =>
                window.open(
                  settings.instagram_club,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="transition hover:text-white"
              aria-label="Instagram"
              title="Instagram"
            >
              <FaInstagram />
            </button>
          )}
          {settings.tiktok_club && (
            <button
              type="button"
              onClick={() =>
                window.open(
                  settings.tiktok_club,
                  "_blank",
                  "noopener,noreferrer",
                )
              }
              className="transition hover:text-white"
              aria-label="TikTok"
              title="TikTok"
            >
              <FaTiktok />
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 overflow-visible">
          <TopBarIcon label="Changer le thème" onClick={onThemeToggle}>
            {theme === "dark" ? <FaSun /> : <FaMoon />}
          </TopBarIcon>

          {isAuthenticated && (
            <>
              <TopBarIcon
                label="Messages"
                onClick={() => navigate("/messages")}
              >
                <FaRegEnvelope
                  className={bumpMessages ? "animate-bell-shake" : ""}
                />
                <TopBarBadge count={unreadMessageCount} />
              </TopBarIcon>

              <TopBarIcon
                label="Notifications"
                onClick={() => navigate("/tableau-de-bord")}
              >
                <FaBell
                  className={bumpNotifications ? "animate-bell-shake" : ""}
                />
                <TopBarBadge count={unreadCount} />
              </TopBarIcon>

              {isAdmin && (
                <TopBarIcon
                  label="Messages du formulaire"
                  onClick={() => navigate("/tableau-de-bord")}
                >
                  <MdOutlineMessage
                    className={bumpContactForms ? "animate-bell-shake" : ""}
                  />
                  <TopBarBadge count={unreadCountContactForm} />
                </TopBarIcon>
              )}
            </>
          )}

          <div className="relative ml-2" ref={userMenuRef}>
            {isAuthenticated ? (
              <>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((previous) => !previous)}
                  className="flex items-center gap-2 rounded-full bg-white/10 py-1 pl-1 pr-3 text-xs font-bold text-white transition hover:bg-white/15"
                >
                  <UserAvatar user={user} />
                  <span className="hidden max-w-[170px] truncate lg:inline">
                    {userFullName}
                  </span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-[calc(100%+10px)] z-50 w-72 overflow-hidden rounded-3xl border border-slate-200 bg-white text-slate-900 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-white">
                    <div className="border-b border-slate-200 px-5 py-4 dark:border-slate-700">
                      <div className="flex items-center gap-3">
                        <UserAvatar user={user} />

                        <div className="min-w-0">
                          <p className="truncate font-bold">{userFullName}</p>

                          {user?.email && (
                            <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                              {user.email}
                            </p>
                          )}

                          <p className="mt-2 inline-flex rounded-full bg-orange-100 px-3 py-1 text-xs font-bold text-orange-800 dark:bg-orange-900/40 dark:text-orange-300">
                            {userRole}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col p-3">
                      <Link
                        to="/profile"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        <FaUser />
                        Mon profil
                      </Link>

                      {isAdmin && (
                        <Link
                          to="/tableau-de-bord"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <FaUserShield />
                          Tableau de bord
                        </Link>
                      )}

                      <button
                        type="button"
                        onClick={onLogout}
                        className="flex items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-bold text-red-600 transition hover:bg-red-50 dark:hover:bg-red-950/30"
                      >
                        <FaSignOutAlt />
                        Déconnexion
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:brightness-110"
                >
                  <FaUserPlus />
                  <span className="hidden lg:inline">Créer un compte</span>
                  <span className="lg:hidden">Compte</span>
                </Link>

                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-white/15"
                >
                  <FaSignInAlt />
                  Connexion
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
